


































































































import { Component, Vue, Prop, Emit, Watch, Model } from 'vue-property-decorator';
import { emptyText, loadingText } from '@/utils/constants';
import { ITabs, IList, IColumn, ISorted } from '@/components/page/types';
import { IConfirmState, IPageParam } from '@/store/types';
import PageBar from '@/components/page/PageBar.vue';
import Confirm from '@/components/dialog/Confirm.vue';

// 带搜索和tab标签的分页列表
@Component({
  components: { PageBar, Confirm },
})
export default class PageList extends Vue {
  @Model('selectChange') public modelData!: IList;

  // 页面同时存在多个本组件时，需要传这个参数，用来判断显示哪个弹窗（配合 confirmProps.name）
  @Prop() public name?: string;

  // 表格的固定高度(可以搭配 fixedHeader 属性，使表头固定)
  @Prop() public height?: string | number;

  // 基于哪个数据字段进行分组
  @Prop() public groupBy!: string;

  // 数据的唯一值，用于 for 循环的 key 值，默认 'id'
  @Prop() public itemKey!: string;

  // 行样式
  @Prop() public rowClass!: () => string;

  // 分页条上，数字按钮最多显示几个
  @Prop() public pageCount?: number;

  // 当前处于第几页
  @Prop({ type: Number, default: 0 }) public pageNum!: number;

  // 每页显示多少条数据
  @Prop({ type: Number, default: 10 }) public pageSize!: number;

  // 分页数据总数
  @Prop({ type: Number, default: 0 }) public pageTotal!: number;

  // 表格数据源
  @Prop({ type: Array, default: () => [] }) public items!: IList;

  // 表头
  @Prop({ type: Array, default: () => [] }) public headers!: IColumn[];

  // 展开的行（当有这个属性时，才会有展开收起功能）
  @Prop({ type: Array, default: () => [] }) public expanded!: IList;

  // 表头的标签页列表数据
  @Prop({ type: Array, default: () => [] }) public tabs!: ITabs;

  // 激活的选项卡索引值（必须使用 .sync 来双向绑定）
  @Prop({ type: Number, default: 0 }) public activeTab!: number;

  // 表格是否处于加载中状态
  @Prop({ type: Boolean, default: false }) public loading!: boolean;

  // 是否允许排序
  @Prop({ type: Boolean, default: false }) public sortable!: boolean;

  // 是否允许多列排序
  @Prop({ type: Boolean, default: false }) public multiSort!: boolean;

  // 是否在表格行的前面显示勾选框
  @Prop({ type: Boolean, default: false }) public showSelect!: boolean;

  // 是否单选
  @Prop({ type: Boolean, default: false }) public singleSelect!: boolean;

  // 禁用分页
  @Prop({ type: Boolean, default: false }) public disablePagination!: boolean;

  // 禁用表头
  @Prop({ type: Boolean, default: false }) public hideDefaultHeader!: boolean;

  // 是否固定表头(必须要配置 height 才能生效, 或者添加类似 .data-table-fixed-10 的 class 也能生效)
  @Prop({ type: Boolean, default: false }) public fixedHeader!: boolean;

  // 确认弹窗的相关属性（主要通过这里的 visible 属性控制显示或隐藏）
  @Prop({ type: Object, default: () => null }) public confirmProps!: IConfirmState;

  public tabIndex = -1;
  public visible = false;
  public selected: IList = [];
  public emptyText = emptyText;
  public loadingText = loadingText;
  public sorted: ISorted = { by: [], desc: [] };

  // 需要合并的列
  public get colspan() {
    const len = this.headers.length;
    return this.showSelect ? len + 1 : len;
  }

  // 表头样式
  public headerColStyle(column: IColumn) {
    const min = parseInt(`${column.min}`, 10);
    const max = parseInt(`${column.max}`, 10);
    const minWidth = isNaN(min) ? null : `${min}px`;
    const maxWidth = isNaN(max) ? null : `${max}px`;
    return (minWidth || maxWidth) ? { display: 'inline-block', minWidth, maxWidth } : null;
  }

  // 单元格样式
  public itemColStyle(column: IColumn) {
    const whiteSpace = column.nowrap ? 'nowrap' : '';
    const wordBreak = column.break ? 'break-all' : '';
    return (whiteSpace || wordBreak) ? { whiteSpace, wordBreak } : null;
  }

  // 排序字段变更时
  public onSortBy(by: string[]) {
    this.sorted = { ...this.sorted, by };
  }

  // 升降序变更时
  public onSortDesc(desc: boolean[]) {
    this.sorted = { ...this.sorted, desc };
  }

  // 排序变更时
  @Emit('sortChange')
  @Watch('sorted')
  public sortDescChange(value: ISorted) {
    return value?.desc.map((item, i) => ({ by: value.by[i], desc: item }));
  }

  // 翻页
  @Emit('pageChange')
  public onPageChange(pagination: IPageParam) {
    return pagination;
  }

  // 设置初始选中的表格数据
  @Watch('modelData', { immediate: true })
  public modelDataChange(selection: IList) {
    this.selected = selection;
  }

  // 表格数据选择变更时
  @Emit('selectChange')
  public onSelectChange(selection: IList) {
    return selection;
  }

  // 设置初始选中的选项卡
  @Watch('activeTab', { immediate: true })
  public activeTabChange(tabIndex: number) {
    this.tabIndex = tabIndex;
  }

  // 通过用户交互更改选项卡时, 为了使用 activeTab.sync 修饰符
  @Emit('update:activeTab')
  public onTabChange(tabIndex: number) {
    return tabIndex;
  }

  // 确认弹窗显隐
  @Watch('confirmProps.visible')
  public confirmPropChange(visible: boolean) {
    const name = this.confirmProps?.name || '';
    this.visible = this.name ? (visible && name === this.name) : visible;
  }

  // 点击确认弹窗
  @Emit('confirm')
  public onConfirm(ok: boolean) {
    return ok;
  }
}
